import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  GitHubRepository,
  EnvVarInput,
  RepositoryBuildInfo,
  RepositoryPathStatus,
} from "@back4app2/sdk/lib/Back4app2";
import { ContainerType, ENV_VAR_FORM } from "../types/containerTypes";

import styles from "../assets/styles/CreateContainerForm.module.css";

import ToggleSwitchCheckbox from "../components/FormInputField/ToggleSwitchCheckbox/ToggleSwitchCheckbox";
import TextInput from "../components/FormInputField/TextInput/TextInput";

import { ReactComponent as PlusIcon } from "../assets/images/plus-icon.svg";
import { ReactComponent as Spinner } from "../assets/images/loading-spinner.svg";
import { ReactComponent as ErrorIcon } from "../assets/images/error-icon.svg";
import { ReactComponent as TrashIcon } from "../assets/images/trash-icon.svg";
import { ReactComponent as VisibilityIcon } from "../assets/images/visibility-icon.svg";
import { ReactComponent as VisibilityOffIcon } from "../assets/images/visibility-off-icon.svg";
import { ReactComponent as FolderIcon } from "../assets/images/folder-icon.svg";

import FormSection from "../components/FormSection/FormSection";
import NoAppDetectedNotice from "../components/BuildMethodSelector/NoAppDetectedNotice";
import RuntimeVersionField, {
  isValidRuntimePin,
} from "../components/BuildMethodSelector/RuntimeVersionField";
import BuildMethodSelector, {
  BuildMethod,
} from "../components/BuildMethodSelector/BuildMethodSelector";
import RepositoryBrowser from "../components/RepositoryBrowser/RepositoryBrowser";
import BranchSelect from "../components/BranchSelect/BranchSelect";
import { pushGTMEvent } from "../utils/gtm";
import back4app2 from "../back4app2";
import { DsButton, Eyebrow } from '../components/ds';

interface ContainerFormProps {
  isCreatingApp: boolean;
  creatingAppErrorMessage: string;
  onCancel: () => void;
  onSubmit: (container: ContainerType) => void;
  selectedRepository: GitHubRepository | undefined;
  // Where the app's code comes from. On the image route the Source and Build
  // Method sections have nothing to configure — the artifact is already built
  // — so they are hidden rather than shown empty. Everything else on this
  // form (name, port, env vars, health check) is the same either way, which
  // is why this is a prop and not a second copy of the file.
  source?: 'GITHUB' | 'DOCKER_HUB';
}
interface ContainerFormType {
  containerName: {
    value: string;
    isValid: Boolean;
    isRequired: Boolean;
    errorMsg: string;
  };
  branchName: {
    value: string;
    isValid: Boolean;
    isRequired: Boolean;
    errorMsg: string;
  };
  rootDirectoryPath: {
    value: string;
    isValid: Boolean;
    isRequired: Boolean;
    errorMsg: string;
  };
  autoDeploy: {
    value: boolean;
  };
  environmentVars: ENV_VAR_FORM[];
  exposedPort: {
    value: string;
    isValid: Boolean;
    isRequired: Boolean;
    errorMsg: string;
  };
  healthCheckEndpoint: {
    value: string;
    isValid: Boolean;
    isRequired: Boolean;
    errorMsg: string;
  };
  runtimeVersion: {
    value: string;
  };
  buildMethod: {
    value: BuildMethod;
  };
  startCommand: {
    value: string;
  };
}

const INITIAL_STATE: ContainerFormType = {
  containerName: {
    value: "",
    isValid: true,
    isRequired: true,
    errorMsg: "",
  },
  branchName: {
    value: "",
    isValid: true,
    isRequired: true,
    errorMsg: "",
  },
  rootDirectoryPath: {
    value: "./",
    isValid: true,
    isRequired: false,
    errorMsg: "",
  },
  autoDeploy: { value: false },
  environmentVars: [],
  exposedPort: {
    value: "",
    isValid: true,
    isRequired: false,
    errorMsg: "",
  },
  healthCheckEndpoint: {
    value: "/",
    isValid: true,
    isRequired: false,
    errorMsg: "",
  },
  buildMethod: { value: "DOCKERFILE" },
  runtimeVersion: { value: "" },
  startCommand: { value: "" },
};

const CreateContainerForm = ({
  isCreatingApp,
  creatingAppErrorMessage,
  onCancel,
  onSubmit,
  selectedRepository,
  source = 'GITHUB',
}: ContainerFormProps) => {
  const isImageSource = source === 'DOCKER_HUB';
  const [container, setContainer] = useState<ContainerFormType>({
    ...INITIAL_STATE,
    branchName: {
      ...INITIAL_STATE.branchName,
      value: selectedRepository?.defaultBranch || "",
    },
  });
  const nameRef = useRef<HTMLInputElement>(null);

  // What the repository has at the current branch + root dir. The detection
  // pre-selects the Build Method; it does not decide it — whatever ends up
  // selected is always submitted explicitly.
  //
  // The last answer and "a detection is running" are separate state on
  // purpose: re-detecting (typing a path, switching branch) keeps the previous
  // answer on screen instead of blanking the section on every keystroke.
  const [buildInfo, setBuildInfo] = useState<RepositoryBuildInfo | null>(null);
  const [isDetecting, setIsDetecting] = useState(true);
  const [isBrowsingRepository, setIsBrowsingRepository] = useState(false);
  const [branches, setBranches] = useState<string[]>([]);
  const [isLoadingBranches, setIsLoadingBranches] = useState(false);
  // Only a list we actually received licenses us to call a typed name wrong.
  // When the listing fails we know nothing about which branches exist, and
  // rejecting the name would block an app over our own outage.
  const [hasBranchList, setHasBranchList] = useState(false);
  // Ignores the answer to a request that is no longer the current one: the
  // repository can change, and Refresh can be pressed twice.
  const branchesRequestRef = useRef(0);

  // Branch names for the picker. A failure here is not fatal: the field is a
  // combobox, so an empty list simply leaves it as free text.
  const loadBranches = useCallback(() => {
    if (!selectedRepository) {
      return;
    }

    const requestId = branchesRequestRef.current + 1;
    branchesRequestRef.current = requestId;

    setIsLoadingBranches(true);

    back4app2
      .repositoryBranches(selectedRepository.id)
      .then((result) => {
        if (branchesRequestRef.current === requestId) {
          setBranches(result);
          setHasBranchList(true);
        }
      })
      .catch((e) => {
        console.error("error listing repository branches", e);

        if (branchesRequestRef.current === requestId) {
          setBranches([]);
          setHasBranchList(false);
        }
      })
      .finally(() => {
        if (branchesRequestRef.current === requestId) {
          setIsLoadingBranches(false);
        }
      });
  }, [selectedRepository]);

  useEffect(() => {
    loadBranches();
  }, [loadBranches]);
  // Detection failed (bad branch, network): fail open to the classic behavior
  // instead of blocking, but the browser has nothing to show.
  const hasDockerfile = buildInfo ? buildInfo.hasDockerfile : null;
  // A path that doesn't exist on this branch (or points at a file) says
  // nothing about Dockerfiles or stacks. The browser reports that instead —
  // locking the Build Method on it would be a lie.
  const isRealDirectory =
    buildInfo?.pathStatus === RepositoryPathStatus.DIRECTORY;
  // Environment-level kill switch: while the route is off, the form behaves
  // exactly as it did before it existed. Fail-closed — an unanswered detection
  // must not advertise a route that would then refuse to build.
  const buildpacksAvailable = !!buildInfo?.buildpackBuildsEnabled;
  // Only meaningful on the buildpack path — a Dockerfile decides its own
  // serving.
  const isStaticSite =
    container.buildMethod.value === "BUILDPACK" && !!buildInfo?.isStaticSite;
  // The folder exists but holds nothing we can deploy. Drives the warning, the
  // card subtitle and hiding the Start Command — three places that would
  // otherwise contradict each other.
  const nothingDetected =
    !isDetecting &&
    isRealDirectory &&
    !!buildInfo &&
    !buildInfo.hasDockerfile &&
    !buildInfo.detectedStack;

  // What the footer counts: the blocking detection plus whatever fields are
  // currently flagged. Fields start valid and only turn invalid once they have
  // been touched, so this never accuses someone of a mistake they have not
  // made yet. It reports; it does not gate — Deploy still validates on submit,
  // exactly as it did before.
  const issueCount = [
    nothingDetected,
    container.containerName.isValid === false,
    container.branchName.isValid === false,
    container.rootDirectoryPath.isValid === false,
    container.exposedPort.isValid === false,
    container.healthCheckEndpoint.isValid === false,
    ...container.environmentVars.map(
      (envVar) => envVar.name.isValid === false || envVar.value.isValid === false
    ),
  ].filter(Boolean).length;
  // Manual Build Method choice is scoped to the path it was made at: browsing
  // to another folder is a change of context, not of preference, so the
  // detection takes over again.
  const userChoseBuildMethodRef = useRef(false);
  // Editing the Start Command, on the other hand, sticks for the whole form —
  // a suggestion never overwrites what the user typed.
  const userEditedStartCommandRef = useRef(false);
  // Clicking a folder is a complete, deliberate change: detect right away.
  // Only typed paths need the debounce.
  const skipDebounceRef = useRef(false);

  useEffect(() => {
    if (!selectedRepository || !container.branchName.value) {
      return;
    }

    let cancelled = false;
    // New branch or new folder: the previous manual choice does not carry over.
    userChoseBuildMethodRef.current = false;

    const delay = skipDebounceRef.current ? 0 : 600;
    skipDebounceRef.current = false;

    const timeout = setTimeout(async () => {
      // The detecting state starts with the request, NOT with the keystroke:
      // branch and root dir are free-text, so flipping it at the top of the
      // effect would dim the section on every character typed while nothing
      // is actually being fetched for another 600ms.
      setIsDetecting(true);

      let result: RepositoryBuildInfo | null = null;

      try {
        result = await back4app2.repositoryBuildInfo(
          selectedRepository.id,
          container.branchName.value,
          container.rootDirectoryPath.value
        );
      } catch (e) {
        console.error("error checking repository build info", e);
      }

      if (cancelled) {
        return;
      }

      // fail-open: with no answer, keep the classic Dockerfile behavior.
      const dockerfileFound = result ? result.hasDockerfile : true;
      const suggestedStartCommand = result?.suggestedStartCommand;

      setBuildInfo(result);
      setIsDetecting(false);
      setContainer((prev) => ({
        ...prev,
        buildMethod: {
          // With the route off, the form behaves as it did before it existed:
          // always Dockerfile. Pre-selecting BUILDPACK here would submit a
          // preference the deploy then refuses.
          value:
            result && !result.buildpackBuildsEnabled
              ? "DOCKERFILE"
              : !dockerfileFound
              ? "BUILDPACK"
              : userChoseBuildMethodRef.current
              ? prev.buildMethod.value
              : "DOCKERFILE",
        },
        // The suggestion only exists when the buildpack would produce no web
        // process at all, so an empty field is the normal case. It never
        // overwrites a command the user typed.
        startCommand: userEditedStartCommandRef.current
          ? prev.startCommand
          : { value: suggestedStartCommand || "" },
      }));
    }, delay);

    return () => {
      cancelled = true;
      clearTimeout(timeout);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedRepository,
    container.branchName.value,
    container.rootDirectoryPath.value,
  ]);

  const onSelectBuildMethod = (value: BuildMethod) => {
    userChoseBuildMethodRef.current = true;
    setContainer((prev) => ({ ...prev, buildMethod: { value } }));
  };

  const onNavigateToDirectory = (newPath: string) => {
    skipDebounceRef.current = true;
    setContainer((prev) => ({
      ...prev,
      rootDirectoryPath: {
        ...prev.rootDirectoryPath,
        value: newPath,
        isValid: true,
        errorMsg: "",
      },
    }));
  };

  const onChangeStartCommand = (newValue: string) => {
    userEditedStartCommandRef.current = true;
    setContainer((prev) => ({ ...prev, startCommand: { value: newValue } }));
  };

  const handleOnChangeEnvVars = (
    id: string,
    newValue: string,
    type: "name" | "value"
  ) => {
    const newEnvVariableList = container.environmentVars.map((env) => {
      if (id === env.id && type === "name") {
        // use only uppercase letters, underscore (_) and numbers.
        // Ref: https://stackoverflow.com/a/2821183/11070995
        let isValid = !!newValue.match(/(^[A-Z_])[A-Z0-9_]+$/);
        let errorMsg = "";
        if (!isValid) {
          errorMsg =
            "Substitution variables must begin with an underscore (_) or uppercase letter and use only uppercase letters and numbers";
        } else {
          errorMsg = "";
        }
        return {
          ...env,
          name: { value: newValue, isValid, errorMsg },
        } as ENV_VAR_FORM;
      } else if (id === env.id && type === "value") {
        let isValid = newValue !== "";
        return {
          ...env,
          value: { ...env.value, value: newValue, isValid },
        } as ENV_VAR_FORM;
      }
      return env;
    });
    setContainer((prev) => ({ ...prev, environmentVars: newEnvVariableList }));
  };

  const handleAddEnviromentVar = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    // validate if already empty entry present
    const emptyVarIdx = container.environmentVars.findIndex(
      (env) => env.name.value === ""
    );
    let newEnvVariableList: ENV_VAR_FORM[] = [...container.environmentVars];
    if (emptyVarIdx !== -1) {
      let newEnvVar: ENV_VAR_FORM = {
        ...container.environmentVars[emptyVarIdx],
      };
      newEnvVar.name.isValid = false;
      newEnvVar.name.errorMsg = "A variable name is must!";
      newEnvVariableList.splice(emptyVarIdx, 1, newEnvVar);
    } else {
      const newEnvVar: ENV_VAR_FORM = {
        id: Math.random().toString(16).slice(2),
        name: { isValid: true, value: "", errorMsg: "" },
        value: { isValid: true, value: "", showValue: false },
      };
      newEnvVariableList = [...container.environmentVars, newEnvVar];
    }
    setContainer((prev) => ({ ...prev, environmentVars: newEnvVariableList }));
  };

  const handleRemoveEnviromentVar = (id: string) => {
    const newEnvVariableList = container.environmentVars.filter(
      (env) => env.id !== id
    );
    setContainer((prev) => ({ ...prev, environmentVars: newEnvVariableList }));
  };

  const toggleEnvVariableVisibilty = (id: string) => {
    const newEnvVariableList = container.environmentVars.map((env) => {
      if (env.id === id) {
        return {
          ...env,
          value: { ...env.value, showValue: !env.value.showValue },
        };
      }
      return env;
    });
    setContainer((prev) => ({ ...prev, environmentVars: newEnvVariableList }));
  };

  const onChangeContainerName = (newValue: string) => {
    // validation
    let isValid = true,
      errorMsg = "";
    if (newValue.trim() === "") {
      isValid = false;
      errorMsg = "Container name is required!";
    }
    setContainer((prev) => ({
      ...prev,
      containerName: {
        ...prev.containerName,
        value: newValue,
        isValid,
        errorMsg,
      },
    }));
  };

  // A name that is not in the repository is not caught at creation today: the
  // app is created, and the failure surfaces much later, in the deploy, as a
  // clone error — after the stack detection already reported "no Dockerfile
  // found" for a branch that never existed. So it is checked here, but only
  // against a list we actually have.
  const validateBranchName = (name: string) => {
    if (name.trim() === "") {
      return {
        isValid: false,
        errorMsg: "Repository branch name is required!",
      };
    }

    if (hasBranchList && branches.length > 0 && !branches.includes(name)) {
      return {
        isValid: false,
        errorMsg: `This repository has no branch named "${name}".`,
      };
    }

    return { isValid: true, errorMsg: "" };
  };

  // The list usually arrives after the field is already filled with the default
  // branch, and a Refresh can change what exists — so the verdict is recomputed
  // whenever the list does.
  useEffect(() => {
    // Never on the image route. There is no branch there, and no field on
    // screen to show a verdict about one — so this only ever marked an empty
    // name invalid and left the error to surface on whatever OTHER field
    // happened to read `branchName`.
    if (isImageSource) {
      return;
    }

    setContainer((prev) => {
      const { isValid, errorMsg } = validateBranchName(prev.branchName.value);

      if (
        prev.branchName.isValid === isValid &&
        prev.branchName.errorMsg === errorMsg
      ) {
        return prev;
      }

      return { ...prev, branchName: { ...prev.branchName, isValid, errorMsg } };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [branches, hasBranchList, isImageSource]);

  const onChangeBranchName = (newValue: string) => {
    // validation
    const { isValid, errorMsg } = validateBranchName(newValue);
    setContainer((prev) => ({
      ...prev,
      branchName: { ...prev.branchName, value: newValue, isValid, errorMsg },
    }));
  };

  const onChangeHealthCheckEndpoint = (newValue: string) => {
    // validation
    let isValid = true,
      errorMsg = "";
    if (newValue[0] !== "/") {
      newValue = `/${newValue}`;
    }
    setContainer((prev) => ({
      ...prev,
      healthCheckEndpoint: {
        ...prev.healthCheckEndpoint,
        value: newValue,
        isValid,
        errorMsg,
      },
    }));
  };

  const onChangeRootDirectorPath = (newValue: string) => {
    // validation
    let isValid = true,
      errorMsg = "";
    newValue = newValue.trim();
    if (!newValue.startsWith("./")) {
      if (newValue.startsWith(".") || newValue.startsWith("/")) {
        newValue = newValue.substring(1);
      }
      newValue = `./${newValue}`;
    }
    setContainer((prev) => ({
      ...prev,
      rootDirectoryPath: {
        ...prev.rootDirectoryPath,
        value: newValue,
        isValid,
        errorMsg,
      },
    }));
  };

  const onChangePort = (newValue: string) => {
    // validation
    let isValid = true,
      errorMsg = "";
    let val = +newValue;
    if (isNaN(val) || !(Number.isInteger(val) && val > 0 && val <= 65535)) {
      isValid = false;
      errorMsg = "Please provide a valid port between 0 to 65535.";
      newValue = "";
    }
    setContainer((prev) => ({
      ...prev,
      exposedPort: { ...prev.exposedPort, value: newValue, isValid, errorMsg },
    }));
  };

  const handleFormSubmit = (e: React.MouseEvent<HTMLButtonElement>) => {
    // validate container name
    if (container.containerName.value === "") {
      nameRef.current?.focus();
      let isValid = false;
      let errorMsg = "Container name is required!";
      setContainer((prev) => ({
        ...prev,
        containerName: { ...prev.containerName, isValid, errorMsg },
      }));
      return;
    }
    // A pin the API would reject: stop here rather than let the whole
    // create-app task fail on a typo in one optional field.
    if (
      container.runtimeVersion.value &&
      !isValidRuntimePin(container.runtimeVersion.value)
    ) {
      return;
    }

    // Same check the field runs, at the last moment it can still stop an app
    // from being created against a branch that does not exist. There is no
    // branch on the image route, and an empty name fails this check — which
    // would block Deploy with no field on screen to explain why.
    if (!isImageSource) {
      const branchCheck = validateBranchName(container.branchName.value);

      if (!branchCheck.isValid) {
        setContainer((prev) => ({
          ...prev,
          branchName: { ...prev.branchName, ...branchCheck },
        }));
        return;
      }
    }

    pushGTMEvent("create_app_containers", true);
    // form containerType value
    let envVars: EnvVarInput[] = container.environmentVars
      .filter((env) => env.name.value !== "")
      .map((env) => ({ key: env.name.value, value: env.value.value }));
    const containerPayload: ContainerType = {
      name: container.containerName.value,
      selectedRepo: selectedRepository,
      // Empty on the image route, and dropped by the caller: there is no
      // branch, no root directory and no build method to send.
      branch: isImageSource ? "" : container.branchName.value,
      rootDirPath: isImageSource ? "" : container.rootDirectoryPath.value,
      autoDeploy: isImageSource ? false : container.autoDeploy.value,
      envVars,
      exposedPort: +container.exposedPort.value,
      healthCheckEndpoint: container.healthCheckEndpoint.value,
      startCommand: isImageSource
        ? undefined
        : container.startCommand.value || undefined,
      builderPreference: isImageSource
        ? undefined
        : container.buildMethod.value,
      // Only ever what someone typed: empty means "follow the repository",
      // which is what an app that was never pinned has to keep meaning.
      runtimeVersion:
        container.buildMethod.value === "BUILDPACK" &&
        container.runtimeVersion.value
          ? container.runtimeVersion.value
          : undefined,
      // Detected, never typed: the command that serves a static site names a
      // file the platform injects at build time, so it stays out of the form.
      // Still sent when the user typed a Start Command — `cnbBuild` consults
      // the flag only for an empty command, so it changes nothing about this
      // build and is what lets clearing the command in Settings later fall
      // back to static serving instead of to no web process at all.
      isStaticSite: isStaticSite || undefined,
    };
    onSubmit(containerPayload);
  };

  if (isCreatingApp) {
    return (
      // A card, like every other block in this step. It used to be a bare
      // centred message that lost its surface the moment the form stopped
      // wrapping everything in one box.
      <div className="rounded-container border border-navy-500 bg-navy-800 px-6 py-14 flex flex-col items-center text-center gap-4">
        <span className="w-11 h-11 flex-none rounded-surface bg-navy-600 border border-navy-500 flex items-center justify-center text-action-soft">
          <Spinner className="animate-spin" width="20px" height="20px" />
        </span>
        <div className="flex flex-col items-center gap-2">
          <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">
            Preparing your environment
          </span>
          {/* Names both halves of the wait. Creating the app also reads the
              repository to work out how to build it, which adds a few seconds
              the first time a commit is seen — a spinner that says only
              "creating app" makes that read as slowness rather than as work. */}
          <p className="m-0 max-w-[46ch] text-ds-135 leading-[1.55] text-navy-200">
            We're setting up the app and reading your repository to work out how to build it.
            The first time we see a commit this takes a few seconds.
          </p>
        </div>
      </div>
    );
  }

  if (creatingAppErrorMessage) {
    return (
      <div className="rounded-container border border-navy-500 bg-navy-800 px-6 py-14 flex flex-col items-center text-center gap-4">
        <span className="w-11 h-11 flex-none rounded-full bg-state-err/[0.14] text-state-err flex items-center justify-center">
          <ErrorIcon width="20px" height="20px" />
        </span>
        <div className="flex flex-col items-center gap-2">
          <span className="font-sora text-ds-15 font-semibold tracking-[-0.01em] text-white">
            {creatingAppErrorMessage}
          </span>
          <p className="m-0 max-w-[46ch] text-ds-135 leading-[1.55] text-navy-200">
            Nothing was created. Go back and try again.
          </p>
        </div>
      </div>
    );
  }

  return (
    // No implicit submission: the form has no submit button, so Enter in any
    // field would fire the browser's default submit and reload the page, which
    // resets the form and reads as "something was sent". Creating an app goes
    // through the Create button and handleFormSubmit, never through Enter.
    <form
      className={`${styles.containerForm} flex flex-col gap-5`}
      onSubmit={(e) => e.preventDefault()}
    >
      {selectedRepository && (
        <RepositoryBrowser
          open={isBrowsingRepository}
          repositoryId={selectedRepository.id}
          branchName={container.branchName.value}
          initialPath={container.rootDirectoryPath.value}
          onClose={() => setIsBrowsingRepository(false)}
          onSelect={onNavigateToDirectory}
        />
      )}
      {/* Two columns on desktop. The name field used to run the full 880px of
          the card for a value that is never longer than a repository name;
          that width now answers the question the form raises — what happens
          when I press Deploy? On mobile the summary drops below the fields. */}
      {/* The blocking state gets a card of its own, on the page ground, above
          the form — the artboard's shape, and the reason it is impossible to
          scroll past. */}
      {nothingDetected ? (
        <div className="rounded-card bg-navy-700 p-4">
          <NoAppDetectedNotice
            rootDirectoryPath={container.rootDirectoryPath.value}
            onBrowseRepository={() => setIsBrowsingRepository(true)}
            buildpacksAvailable={buildpacksAvailable}
          />
        </div>
      ) : null}

      <div className="grid gap-3 lg:gap-0 lg:grid-cols-[minmax(0,1fr)_320px] lg:rounded-card lg:border lg:border-navy-500 lg:bg-navy-800 lg:overflow-hidden">
      <div className="flex flex-col gap-3 lg:block lg:px-5 lg:py-[18px]">
        <FormSection title="Application">
          <TextInput
            value={container.containerName.value}
            label="Name"
            name={"name"}
            onChange={(e) => onChangeContainerName(e.target.value)}
            placeholderText="Give a name"
            ref={nameRef}
            isError={!container.containerName.isValid}
            errorMessage={container.containerName.errorMsg}
            isRequired={container.containerName.isRequired}
          />
        </FormSection>
        {/* Both sections describe how source becomes an image. The image
            route has no source to point at and nothing to build, so they
            are hidden rather than shown empty — everything below this,
            from the port to the health check, applies either way. */}
        {!isImageSource && (<>
          {/* Source comes before Build Method on purpose: the branch and the root
              directory are the inputs the detection runs on, so the user has to
              be able to fix the folder before reading the detection result. */}
          <FormSection
            title="Source"
            infoText="Where your web application's code lives."
          >
            <div className="grid grid-cols-2 gap-x-4 gap-y-4 [@media(max-width:767.98px)]:grid-cols-1">
              {/* Typeable on purpose — see BranchSelect: the listing is capped
                  and can fail, and neither must block creating an app. */}
              <BranchSelect
                value={container.branchName.value}
                branches={branches}
                onChange={onChangeBranchName}
                onRefresh={loadBranches}
                isLoading={isLoadingBranches}
                isError={!container.branchName.isValid}
                errorMessage={container.branchName.errorMsg}
              />
              {/* Browse is always offered, whatever the repository looks like.
                  It is deliberately NOT disabled when the current path has no
                  subfolders: we only learn that after a detection round, so the
                  control would go grey a second after loading — and an empty
                  listing is also what a mistyped path produces, exactly when the
                  browser is the way out. The modal says so in words instead. */}
              <TextInput
                value={container.rootDirectoryPath.value}
                label="Root directory"
                name="rootDirPath"
                onChange={(e) => onChangeRootDirectorPath(e.target.value)}
                placeholderText="./"
                infoText="By default, the root directory is the repository's root. If you specify a different directory, Back4app runs all commands within that directory only and ignores any changes outside it."
                isError={!container.rootDirectoryPath.isValid}
                errorMessage={container.rootDirectoryPath.errorMsg}
                endAdornment={
                  <button
                    type="button"
                    className={`${styles.groupInputAddBtn} whitespace-nowrap`}
                    onClick={() => setIsBrowsingRepository(true)}
                  >
                    <FolderIcon /> Browse folders
                  </button>
                }
              />
            </div>
          </FormSection>
          {/* Nothing deployable here: the section is not rendered at all. There
              is no choice left to offer — Dockerfile is already disabled (there
              is none) and Buildpacks has nothing to build — so a radio group
              here would only ask the user to pick between two dead ends. The
              notice itself has moved to the top of the step, above this card,
              because a blocking state read halfway down a scroll is a blocking
              state nobody reads. The submitted method is unaffected: the
              detection already falls back to BUILDPACK when no Dockerfile is
              found. */}
          {nothingDetected ? null : (
          <FormSection
            title="Build Method"
            infoText="How Back4app builds your web application's image."
          >
            <div className="space-y-4">
              {(
                <BuildMethodSelector
                  value={container.buildMethod.value}
                  onChange={onSelectBuildMethod}
                  buildpacksAvailable={buildpacksAvailable}
                  isDetecting={isDetecting}
                  hasDetectionResult={hasDockerfile !== null}
                  dockerfileDisabled={isRealDirectory && hasDockerfile === false}
                  dockerfileSubtitle={
                    isRealDirectory && hasDockerfile === false
                      ? "No Dockerfile in this folder."
                      : undefined
                  }
                  detectedStack={buildInfo?.detectedStack}
                  showDockerfileIgnoredWarning={hasDockerfile === true}
                />
              )}
              {/* Detection stated as a default, not as a verdict. The Start
                  Command below is the way out of it: `suggestStartCommand`
                  returns nothing for a static site, so the field arrives empty,
                  and `cnbBuild` reads `isStaticSite` only while it stays empty.
                  Typing a command therefore wins, clearing it goes back to
                  static serving — the same bargain Settings already offers. */}
              {isStaticSite && !nothingDetected && (
                <div className="text-ds-115 leading-[1.5] text-navy-200">
                  This looks like a static site — we'll serve the built files for
                  you. Set a Start Command below to run your own server instead.
                </div>
              )}
              {/* Only on the buildpack path: a Dockerfile names its own runtime
                  in its FROM line, and a field that silently did nothing there
                  would be worse than no field. */}
              {container.buildMethod.value === "BUILDPACK" && !nothingDetected && (
                <RuntimeVersionField
                  detectedStack={buildInfo?.detectedStack}
                  resolved={buildInfo?.runtimeVersion}
                  value={container.runtimeVersion.value}
                  onChange={(value) =>
                    setContainer((prev) => ({
                      ...prev,
                      runtimeVersion: { value },
                    }))
                  }
                />
              )}
              {/* Asking how to start an app we haven't found is noise — hence
                  the `nothingDetected` gate. A static site is the opposite
                  case: the detection is a guess the user may disagree with, and
                  a repository whose server we failed to see had no way to say
                  so here while Settings, on the very same app, offered the
                  field and told them to use it. */}
              {container.buildMethod.value === "BUILDPACK" &&
                !nothingDetected && (
                  <TextInput
                    value={container.startCommand.value}
                    label="Start Command"
                    name="startCommand"
                    onChange={(e) => onChangeStartCommand(e.target.value)}
                    placeholderText="e.g. gunicorn app:app"
                    infoText={
                      isStaticSite
                        ? "Optional. Leave empty and we'll serve your build output as a static site. Set it to run a server of your own instead. The app must listen on the PORT env var (or set the Port below)."
                        : "Optional. Leave empty and we'll use your Procfile or start script. Set it when your app has neither. The app must listen on the PORT env var (or set the Port below)."
                    }
                    isError={false}
                    errorMessage=""
                  />
                )}
            </div>
          </FormSection>
          )}
        </>)}
        <FormSection title="Deploy">
          <div className="space-y-4">
            <TextInput
              value={`${container.exposedPort.value}`}
              label="Port"
              name="exposedPort"
              onChange={(e) => onChangePort(e.target.value)}
              placeholderText="8080"
              infoText="The port your app listens on. Leave it empty and Back4app detects it automatically."
              isError={!container.exposedPort.isValid}
              errorMessage={container.exposedPort.errorMsg}
            />

            {/* Gone, not disabled, on the image route: there is no push to
                listen to, so a greyed toggle would read as a feature we took
                away rather than one that does not apply. The line below says
                what happens instead. */}
            {isImageSource ? (
              <div className="text-ds-115 leading-[1.5] text-navy-200">
                Deploys are manual on this route. Publish a new image and
                redeploy from the app's page when you want it picked up.
              </div>
            ) : (
              <ToggleSwitchCheckbox
                value={container.autoDeploy.value}
                onChange={() =>
                  setContainer((prev) => ({
                    ...prev,
                    autoDeploy: { value: !prev.autoDeploy.value },
                  }))
                }
                labelText="Autodeploy"
                description="Deploy on every repository push. Off means you trigger every deploy yourself."
                disabled={true}
                infoText="This feature is available on paid plans. Upgrade your plan to automatically deploy your application whenever changes are pushed to your repository."
              />
            )}
          </div>
        </FormSection>
        {/* <div className="h-4 border-t-light-blue border-opacity-20 border-t"/> */}
        <FormSection
          title="Environment Variables"
          badge={<span className="font-courrier text-ds-105 uppercase tracking-[0.08em] leading-none px-1.5 py-[3px] rounded-[4px] border border-navy-400 text-navy-200">optional</span>}
          infoText="Use environment variables to store API keys, configuration values, and other sensitive data."
        >
          {/* A helper line, at helper weight. Inheriting the panel's body
              colour made it plain white — louder than the section label above
              it and than the field labels below, so the eye landed on a
              sentence nobody needs to read twice. */}
          <div className="text-ds-115 leading-[1.5] text-navy-200 mb-4">
            Your App will have access to these variables at both build time and
            runtime.
          </div>
          {/* Enviroment variable list */}
          {container.environmentVars.map((enviromentVar) => {
            const { id, name, value } = enviromentVar;
            return (
              <div className="flex [@media(max-width:767.98px)]:flex-col justify-between items-start [@media(max-width:767.98px)]:items-stretch gap-3 md:gap-0 mb-8" key={id}>
                <div className={styles.inputGroupDiv + " flex-1"}>
                  <TextInput
                    value={name.value}
                    label="Name"
                    name={`env-name-${id}`}
                    onChange={(e) =>
                      handleOnChangeEnvVars(id, e.target.value.trim(), "name")
                    }
                    placeholderText="EXAMPLE_NAME"
                    isError={!name.isValid}
                    errorMessage={name.errorMsg}
                  />
                  <TextInput
                    type={value.showValue ? "text" : "password"}
                    value={value.value}
                    label="Value"
                    name={`env-value-${id}`}
                    onChange={(e) =>
                      handleOnChangeEnvVars(id, e.target.value, "value")
                    }
                    placeholderText="A1B2C3D4E5F6"
                    endAdornment={
                      <span
                        className="cursor-pointer"
                        onClick={() => toggleEnvVariableVisibilty(id)}
                      >
                        {value.showValue ? (
                          <VisibilityOffIcon />
                        ) : (
                          <VisibilityIcon />
                        )}
                      </span>
                    }
                    helpText={value.showValue ? "Will be encrypted." : ""}
                  />
                </div>
                <span
                  className="b4-focus text-state-err flex justify-center items-center gap-2 px-3 border border-state-err/60 rounded-control hover:bg-state-err/10 hover:cursor-pointer h-[38px] ml-4 mt-8 [@media(max-width:767.98px)]:ml-0 [@media(max-width:767.98px)]:mt-0 [@media(max-width:767.98px)]:w-full text-center transition-colors duration-[120ms]"
                  onClick={() => handleRemoveEnviromentVar(id)}
                >
                  <TrashIcon />
                  <span className="hidden [@media(max-width:767.98px)]:inline text-sm">Remove variable</span>
                </span>
              </div>
            );
          })}
          {/* Add button for Environment varible */}
          <a
            onClick={handleAddEnviromentVar}
            type="button"
            href="/#"
            className={styles.groupInputAddBtn}
          >
            {" "}
            <PlusIcon /> Add variable
          </a>
        </FormSection>
        <FormSection title="Health" badge={<span className="font-courrier text-ds-105 uppercase tracking-[0.08em] leading-none px-1.5 py-[3px] rounded-[4px] border border-navy-400 text-navy-200">optional</span>}>
          <div className="space-y-4">
            <TextInput
              value={container.healthCheckEndpoint.value}
              label="Health check path"
              name="healthCheckEndpoint"
              onChange={(e) => onChangeHealthCheckEndpoint(e.target.value)}
              placeholderText="/checkpath"
              infoText="A health check is an HTTP request performed during deployments to confirm that a service is healthy."
              // Its OWN validity, not the branch's. Copy-paste from the branch
              // field above meant the health input reported "Repository branch
              // name is required!" — and on the container-image route, where
              // there is no branch at all, it said so permanently, about a
              // field that was perfectly valid. Invisible while this section
              // was a closed accordion; the always-open sections surfaced it.
              isError={!container.healthCheckEndpoint.isValid}
              errorMessage={container.healthCheckEndpoint.errorMsg}
            />
          </div>
        </FormSection>
      </div>

        <DeploySummary
          repositoryFullName={selectedRepository?.fullName}
          branchName={container.branchName.value}
          rootDirectoryPath={container.rootDirectoryPath.value}
          exposedPort={container.exposedPort.value}
          autoDeploy={container.autoDeploy.value}
          buildMethod={container.buildMethod.value}
          detectedStack={buildInfo?.detectedStack}
          nothingDetected={nothingDetected}
        />
      </div>

      {/* Pinned to the base on a phone. Deploy at the end of a long scroll is
          Deploy nobody finds; the count beside it says why it is worth
          scrolling back up. Cancel is dropped there because the header's back
          arrow already calls the same `onCancel`. */}
      {/* Reserves the height the fixed bar covers, so the last field of the
          form is never behind it. */}
      <div aria-hidden="true" className="h-[76px]" />

      {/* Fixed rather than sticky. Sticky only pins while there is content
          below it, so on a short step — the image route's first screen is two
          fields — Deploy came to rest wherever the form happened to end,
          halfway up the page. The inner column repeats `.container`'s measure
          so the buttons stay on the same grid as the fields above them. */}
      <div className="fixed bottom-0 inset-x-0 z-20 bg-navy-800 border-t border-navy-500 [padding-bottom:env(safe-area-inset-bottom)]">
        <div className="w-full max-w-[1120px] mx-auto px-4 py-3.5 flex items-center justify-end gap-3">
        {issueCount > 0 ? (
          <span className="mr-auto font-courrier text-ds-115 text-state-warn">
            {issueCount} issue{issueCount === 1 ? '' : 's'} to fix
          </span>
        ) : null}
        {/* DsButton rather than the shared Button: that one brings its own
            padding from Button.module.css, which fought the height set here and
            left Cancel and Deploy at different sizes. This one is 38px and
            flex-centred by construction. */}
        <DsButton variant="outline" size="lg" onClick={onCancel} className="hidden sm:inline-flex">
          Cancel
        </DsButton>
        <DsButton
          variant="primary"
          size="touch"
          onClick={handleFormSubmit}
          className="flex-none lg:!h-[38px]"
        >
          Deploy
        </DsButton>
        </div>
      </div>
    </form>
  );
};

/**
 * The right-hand column: what pressing Deploy will actually do, in the same
 * eyebrow-and-value shape the publish drawer uses. It answers before the click
 * rather than after it.
 */
const DeploySummary = ({
  repositoryFullName,
  branchName,
  rootDirectoryPath,
  exposedPort,
  autoDeploy,
  buildMethod,
  detectedStack,
  nothingDetected,
}: {
  repositoryFullName?: string,
  branchName?: string,
  rootDirectoryPath?: string,
  exposedPort?: string,
  autoDeploy?: boolean,
  buildMethod?: BuildMethod,
  detectedStack?: string,
  nothingDetected?: boolean,
}) => {
  const rootDir = rootDirectoryPath || './';

  // Reads the method that is actually selected. Hardcoding "Dockerfile" here
  // told someone who had just picked Buildpacks that we were about to do the
  // other thing.
  const build = nothingDetected
    ? 'nothing to build here yet'
    : buildMethod === 'BUILDPACK'
      ? `${detectedStack ? `${detectedStack} buildpack` : 'Buildpacks'} in ${rootDir}`
      : `Dockerfile in ${rootDir}`;

  const rows = [
    {
      label: 'source',
      value: [repositoryFullName?.split('/').pop(), branchName].filter(Boolean).join(' · ') || '—',
      mono: true,
    },
    {
      label: 'build',
      value: build,
      mono: true,
    },
    {
      // An empty port field means "detect it", not 8080 — the placeholder is a
      // hint, not a value, and printing it as one is a promise we do not keep.
      label: 'serve',
      value: `port ${exposedPort || 'auto'}, public URL`,
      mono: true,
    },
    {
      label: 'on push',
      value: autoDeploy ? 'deploy automatically' : 'manual deploys only',
      mono: false,
    },
  ];

  return (
    <aside className="rounded-container border border-navy-500 bg-navy-800 p-4 lg:rounded-none lg:border-0 lg:border-l lg:border-navy-700 lg:bg-[#0F1A2C] lg:px-5 lg:py-[18px] flex flex-col gap-3.5">
      <Eyebrow>what will happen</Eyebrow>
      <div className="-mx-1">
        {rows.map((row, i) => (
          <div key={row.label} className={`flex flex-col gap-1 py-3 ${i ? 'border-t border-navy-500' : ''}`}>
            <Eyebrow tight>{row.label}</Eyebrow>
            <span className={`text-white leading-[1.4] ${row.mono ? 'font-courrier text-ds-125' : 'text-[13px]'}`}>
              {row.value}
            </span>
          </div>
        ))}
      </div>
    </aside>
  );
};

export default CreateContainerForm;
